import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import ExpandIcon from '@/assets/icons/dropdown-arrow.svg?react';
import EditIcon from '@/assets/icons/pencil.svg?react';
import { TeamGroupDetails } from '@/components/organisms/TeamGroupDetails';
import { Group } from '@/DTOs/groups';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { blockPropagation } from '@/utility/blockPropagation';

interface Props {
  readonly group: Group;
  readonly expandedGroupId: string | false;
  readonly editingGroupId: string | false;

  readonly onExpand: React.Dispatch<React.SetStateAction<string | false>>;
}

export const GroupListItem: React.FC<Props> = (props: Props): React.ReactElement => {
  const { group, expandedGroupId, editingGroupId, onExpand } = props;
  const { groupId } = group;

  const navigate = useNavigate();
  const toast = useToast();
  const expanded = React.useMemo((): boolean => {
    return (expandedGroupId === groupId && editingGroupId === false) || editingGroupId === groupId;
  }, [editingGroupId, expandedGroupId, groupId]);

  const mode = React.useMemo(
    (): 'edit' | 'view' => (editingGroupId === groupId ? 'edit' : 'view'),
    [editingGroupId, groupId],
  );
  const editable = group.isIntegratorGroup;
  const tr = useTranslator();

  const onUpdated = React.useCallback((): void => {
    toast.show(tr('GROUP_UPDATED'), 'success');
    navigate('/settings/users/roles');
  }, [navigate, toast, tr]);

  const onAccordionSummaryClick = (event: React.MouseEvent): void => {
    if (editingGroupId) {
      event.stopPropagation();
      event.preventDefault();

      return;
    }

    onExpand((expandedGroupId: string | false): string | false => {
      if (expandedGroupId === group.groupId) {
        return false;
      }

      return group.groupId;
    });
  };

  return (
    <ListItem key={group.groupId} disablePadding={true} disableGutters={true} divider={true}>
      <StyledAccordion expanded={expanded}>
        <StyledAccordionSummary
          component="div"
          expandIcon={
            <IconButton color="inherit" onClick={onAccordionSummaryClick}>
              <ExpandIcon color="currentColor" />
            </IconButton>
          }>
          <Typography variant="h7">
            <Typography variant="inherit" component="span">
              {tr(group.groupName)}
            </Typography>
            {editable && (
              <Typography variant="inherit" component="span" color="text.secondary">
                {' '}
                ({tr('CUSTOM')})
              </Typography>
            )}
          </Typography>

          {editable && (
            <Box sx={styles.integratorGroup} onClick={blockPropagation}>
              {mode === 'view' && (
                <IconButton color="inherit" component={Link} to={`edit/${group.groupId}`}>
                  <EditIcon fill="currentColor" />
                </IconButton>
              )}
            </Box>
          )}
        </StyledAccordionSummary>

        <StyledAccordionDetails>
          <TeamGroupDetails mode={mode} group={group} onUpdated={onUpdated} />
        </StyledAccordionDetails>
      </StyledAccordion>
    </ListItem>
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/typedef
const StyledAccordion = styled(Accordion)(({ theme }) => ({
  paddingTop: theme.spacing(2.5),
  paddingBottom: theme.spacing(2.5),
  paddingLeft: 0,
  paddingRight: 0,
  width: '100%',

  '& .MuiAccordionSummary-root.MuiButtonBase-root': {
    cursor: 'default',
    padding: 0,
  },

  '&.MuiPaper-root': {
    borderRadius: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)<AccordionSummaryProps>`
  height: 24px;
  line-height: 24px;
  min-height: 0;

  &.Mui-expanded {
    min-height: 0;
    height: 24px;
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  padding: 8px 0;
`;

const styles: Record<string, SystemStyleObject> = {
  roleItem: {
    display: 'flex',
    alignItems: 'center',
    mb: 0.5,
  },
  groupContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    width: '100%',
  },
  integratorGroup: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'text.primary',
    width: 24,
    height: 24,
    mx: 1,
  },
  buttonsContainer: {
    mt: 2,
    display: 'flex',
    gap: 1,
  },
};
